import config from '../config.json'
import { saveClientServices } from './clientServices'

const SESSION_STORAGE_KEY = 'pymego_session'

// La sesion vive en localStorage para que sobreviva al cierre de la app: el token
// dura 12h y su caducidad se valida en cada arranque, asi que una caida del
// servidor de login no deja afuera a quien ya habia entrado.
function readStoredSession() {
  return (
    localStorage.getItem(SESSION_STORAGE_KEY) ||
    sessionStorage.getItem(SESSION_STORAGE_KEY)
  )
}

function writeStoredSession(value) {
  localStorage.setItem(SESSION_STORAGE_KEY, value)
  sessionStorage.removeItem(SESSION_STORAGE_KEY)
}

function removeStoredSession() {
  localStorage.removeItem(SESSION_STORAGE_KEY)
  sessionStorage.removeItem(SESSION_STORAGE_KEY)
}

let onSessionExpired = null
let sessionExpiredHandled = false
let nativeFetch = null

function requestHasToken(input, init = {}) {
  if (init.headers) {
    if (init.headers instanceof Headers) {
      return init.headers.has('token')
    }
    return Boolean(init.headers.token)
  }

  if (input instanceof Request) {
    return input.headers.has('token')
  }

  return false
}

function isStoredTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))

    if (!payload.exp) return false

    return Date.now() >= payload.exp * 1000
  } catch (_) {
    return true
  }
}

export function saveSession() {
  if (!config.token || !config.apiUrl || !config.auth) return

  const session = {
    token: config.token,
    auth: config.auth,
    name: config.name,
    apiUrl: config.apiUrl,
    services: config.services,
    client: config.client,
  }

  writeStoredSession(JSON.stringify(session))
  saveClientServices(config.client, config.services)
}

export function restoreSession() {
  const raw = readStoredSession()

  if (!raw) return false

  try {
    const session = JSON.parse(raw)

    if (!session.token || !session.apiUrl || !session.auth) {
      removeStoredSession()
      return false
    }

    if (isStoredTokenExpired(session.token)) {
      removeStoredSession()
      return false
    }

    config.token = session.token
    config.auth = session.auth
    config.name = session.name
    config.apiUrl = session.apiUrl
    config.services = session.services

    if (session.client) {
      config.client = session.client
      localStorage.setItem('client', session.client)
      saveClientServices(session.client, session.services)
    }

    return true
  } catch (_) {
    removeStoredSession()
    return false
  }
}

export function clearSession({ keepClient = true } = {}) {
  delete config.token
  delete config.auth
  delete config.name
  delete config.apiUrl
  delete config.services

  removeStoredSession()

  if (keepClient) {
    const client = localStorage.getItem('client')
    localStorage.removeItem('user')
    if (client) {
      localStorage.setItem('client', client)
    }
  } else {
    localStorage.clear()
    config.client = null
  }
}

export function resetSessionExpiredState() {
  sessionExpiredHandled = false
}

export function setSessionExpiredHandler(handler) {
  onSessionExpired = handler
}

function handleSessionExpired() {
  if (sessionExpiredHandled) return

  sessionExpiredHandled = true
  clearSession()

  if (typeof onSessionExpired === 'function') {
    onSessionExpired()
  }
}

export function setupAuthFetchInterceptor() {
  if (nativeFetch) return

  nativeFetch = window.fetch.bind(window)

  window.fetch = async function authFetch(input, init = {}) {
    const response = await nativeFetch(input, init)

    if (!config.token || !requestHasToken(input, init)) {
      return response
    }

    const contentType = response.headers.get('content-type') || ''

    if (!contentType.includes('application/json')) {
      return response
    }

    try {
      const data = await response.clone().json()

      if (data?.code === 401) {
        handleSessionExpired()
      }
    } catch (_) {
      // Ignore non-JSON or malformed responses.
    }

    return response
  }
}
